// Web-compatible file upload utility
const IS_DEV = import.meta.env.DEV;
const getApiUrl = (path: string) => IS_DEV ? path : `https://api.s3protection.com${path}`;

export async function uploadFile(args: {
  url: string;
  method?: string;
  fields?: Record<string, string>;
  fileFieldName?: string;
  fileName: string;
  contentType: string;
  dataUrl: string;
}): Promise<{ ok: boolean; status: number; text: string }> {
  const { url, method = 'POST', fields = {}, fileFieldName = 'file', fileName, contentType, dataUrl } = args;
  
  // Convert URL to use proxy in dev mode
  const apiUrl = IS_DEV ? url.replace('https://api.s3protection.com', '') : url;
  
  // Convert data URL to Blob
  const base64Data = dataUrl.split(',')[1];
  const binaryData = atob(base64Data);
  const bytes = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    bytes[i] = binaryData.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: contentType });
  
  // Create FormData
  const formData = new FormData();
  formData.append(fileFieldName, blob, fileName);
  
  // Add additional fields
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  
  try {
    const response = await fetch(apiUrl, {
      method,
      body: formData,
      credentials: 'include'
    });
    
    const text = await response.text();
    
    return {
      ok: response.ok,
      status: response.status,
      text
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      text: error instanceof Error ? error.message : 'Upload failed'
    };
  }
}

// Fetch an image and convert to data URL (for authenticated requests)
export async function fetchAsDataUrl(url: string): Promise<{
  ok: boolean;
  status: number;
  dataUrl: string | null;
  contentType: string | null;
}> {
  try {
    // Convert URL to use proxy in dev mode
    const apiUrl = IS_DEV ? url.replace('https://api.s3protection.com', '') : url;
    
    const response = await fetch(apiUrl, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        dataUrl: null,
        contentType: null
      };
    }
    
    const blob = await response.blob();
    const contentType = response.headers.get('content-type');
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({
          ok: true,
          status: response.status,
          dataUrl: reader.result as string,
          contentType
        });
      };
      reader.onerror = () => {
        resolve({
          ok: false,
          status: response.status,
          dataUrl: null,
          contentType
        });
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return {
      ok: false,
      status: 0,
      dataUrl: null,
      contentType: null
    };
  }
}
